import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { Usuario } from '../types'
import { authUtils, authService, usuarioService } from '../services/auth'
import { useSessionTimeout } from '../hooks/useSessionTimeout'

interface AuthContextType {
  user: Usuario | null
  isAuthenticated: boolean
  isAdmin: boolean
  isUser: boolean
  userRole: 'ADMIN' | 'USER' | null
  isLoading: boolean
  login: (user: Usuario) => void
  logout: () => void
  updateUser: (user: Usuario) => void
  sessionTimeout: {
    resetTimeout: () => void
    getTimeRemaining: () => number
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<Usuario | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isAuthenticated = !!user
  
  // Configurar timeout de sessão (será atualizado dinamicamente)
  const [timeoutMinutes, setTimeoutMinutes] = useState(60)
  const sessionTimeout = useSessionTimeout({ 
    timeoutMinutes,
    warningMinutes: 5,
    onLogout: () => {
      authUtils.clearAuthData()
      setUser(null)
    },
    isAuthenticated: !!user
  })

  // Carregar tempo de sessão do backend (opcional)
  useEffect(() => {
    const loadTempoSessao = async () => {
      try {
        const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8888/api'
        const token = localStorage.getItem('authToken')
        if (!token || token === 'null') {
          console.log('Token não encontrado, usando valor padrão (60 min)')
          setTimeoutMinutes(60)
          return
        }

        const response = await fetch(`${apiUrl}/configuracoes/tempo-sessao`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          setTimeoutMinutes(data.tempoSessaoMinutos || 60)
        } else if (response.status === 403 || response.status === 404) {
          // Endpoint não existe ou não está acessível - usar valor padrão
          console.log('Endpoint de configurações não disponível, usando valor padrão (60 min)')
          setTimeoutMinutes(60)
        }
      } catch (error) {
        // Erro de rede ou outro problema - usar valor padrão
        console.log('Erro ao carregar tempo de sessão, usando valor padrão (60 min):', error)
        setTimeoutMinutes(60)
      }
    }

    if (isAuthenticated && user?.role === 'ADMIN') {
      loadTempoSessao()
    }
  }, [isAuthenticated])

  // Flag para evitar múltiplas renovações simultâneas
  const renovandoToken = useRef(false)
  const lastRenovacaoRef = useRef<number>(0)
  
  // Throttling: máximo 1 chamada por minuto (60 segundos)
  const THROTTLE_MS = 60000 // 60 segundos

  // Função para renovar token com throttling (seguindo recomendação do backend)
  const renovarTokenComThrottle = useCallback(async () => {
    if (renovandoToken.current) return
    if (!isAuthenticated) return

    // Throttling: só tenta renovar se passou pelo menos 1 minuto
    const agora = Date.now()
    if (agora - lastRenovacaoRef.current < THROTTLE_MS) {
      return // Muito recente, ignora
    }

    lastRenovacaoRef.current = agora
    renovandoToken.current = true

    try {
      const response = await authService.renewToken()
      
      if (response.token) {
        // Token foi renovado - atualizar
        localStorage.setItem('authToken', response.token)
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken)
        }
        if (response.usuario) {
          localStorage.setItem('user', JSON.stringify(response.usuario))
          setUser(response.usuario)
        }
        console.log('Token renovado automaticamente')
      }
      // Se não renovou, significa que ainda tem mais de 5 minutos (ok)
      // Não precisa fazer nada, o backend já retornou a mensagem
    } catch (error: any) {
      console.error('Erro ao renovar token:', error)
      // Se o token está inválido/expirado, não fazer nada aqui
      // O interceptor do axios vai tratar o erro 401
    } finally {
      renovandoToken.current = false
    }
  }, [isAuthenticated])

  // Renovar token em atividade do usuário (com throttling)
  useEffect(() => {
    if (!isAuthenticated) return

    // Adicionar listeners para clicks e keypress (sem mousemove)
    document.addEventListener('click', renovarTokenComThrottle)
    document.addEventListener('keypress', renovarTokenComThrottle)

    return () => {
      document.removeEventListener('click', renovarTokenComThrottle)
      document.removeEventListener('keypress', renovarTokenComThrottle)
    }
  }, [isAuthenticated, renovarTokenComThrottle])

  useEffect(() => {
    // Verificar se há dados de autenticação salvos
    const loadUserData = async () => {
      const savedUser = authUtils.getCurrentUser()

      if (savedUser && authUtils.isAuthenticated()) {
        // Usar dados salvos primeiro (especialmente importante após login OAuth2)
        setUser(savedUser)
        setIsLoading(false)
        
        // Tentar buscar dados atualizados do backend em background (opcional)
        // Mas não bloquear se falhar - usar dados salvos
        // Adicionar delay para evitar requisições muito rápidas após login OAuth2
        const token = localStorage.getItem('authToken')
        if (token && token !== 'null' && token !== 'undefined' && savedUser.id) {
          // Aguardar um pouco antes de buscar dados atualizados
          // Isso evita problemas quando o backend ainda está processando o login
          setTimeout(() => {
            usuarioService.getUsuarioById(savedUser.id)
              .then((usuarioAtualizado) => {
                if (usuarioAtualizado) {
                  setUser(usuarioAtualizado)
                  localStorage.setItem('user', JSON.stringify(usuarioAtualizado))
                }
              })
              .catch((error: any) => {
                // Apenas logar erro, mas não limpar dados se for erro de rede/CORS
                const status = error?.response?.status
                const isNetworkError = error?.code === 'ERR_NETWORK' || 
                                     error?.message?.includes('CORS') ||
                                     error?.message?.includes('Network Error')
                
                if (status === 401 || status === 403) {
                  // Apenas limpar se for erro de autenticação/autorização
                  console.warn('Token inválido ou expirado, limpando dados')
                  authUtils.clearAuthData()
                  setUser(null)
                  window.dispatchEvent(new CustomEvent('auth-logout'))
                } else if (!isNetworkError) {
                  // Logar apenas se não for erro de rede (que é esperado em alguns casos)
                  console.warn('Erro ao buscar dados atualizados do usuário (usando dados salvos):', error)
                }
                // Se for erro de rede/CORS, apenas usar dados salvos (não fazer nada)
              })
          }, 2000) // Aguardar 2 segundos antes de buscar dados atualizados
        }
      } else {
        // Não há usuário válido salvo - garantir limpeza
        authUtils.clearAuthData()
        setUser(null)
        setIsLoading(false)
      }
    }
    
    loadUserData()

    // Escutar eventos de autenticação
    const handleAuthLogin = (event: CustomEvent) => {
      const userData = event.detail
      if (userData) {
        // Garantir que os dados estão salvos no localStorage
        if (userData.id) {
          localStorage.setItem('user', JSON.stringify(userData))
        }
        // Definir usuário e marcar como não carregando
        setUser(userData)
        setIsLoading(false)
      } else {
        // Se não há detail, verificar localStorage
        const savedUser = authUtils.getCurrentUser()
        const token = authUtils.getToken()
        if (savedUser && token) {
          setUser(savedUser)
          setIsLoading(false)
        } else {
          // Se não há dados salvos, manter loading como false mas sem usuário
          setIsLoading(false)
        }
      }
    }

    const handleAuthLogout = () => {
      setUser(null)
      setIsLoading(false)
    }

    window.addEventListener('auth-login', handleAuthLogin as EventListener)
    window.addEventListener('auth-logout', handleAuthLogout)
    
    return () => {
      window.removeEventListener('auth-login', handleAuthLogin as EventListener)
      window.removeEventListener('auth-logout', handleAuthLogout)
    }
  }, [])

  const login = (userData: Usuario) => {
    setUser(userData)
  }

  const logout = () => {
    authUtils.clearAuthData()
    setUser(null)
  }

  const updateUser = (userData: Usuario) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  const isAdmin = user?.role === 'ADMIN'
  const isUser = user?.role === 'USER'
  const userRole = user?.role || null

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isAdmin,
    isUser,
    userRole: userRole as 'ADMIN' | 'USER' | null,
    isLoading,
    login,
    logout,
    updateUser,
    sessionTimeout, // Expor o hook de timeout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

export default AuthContext
